import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { card, Btn, Field, Input, Textarea, Select, ImageUpload } from "./ui";
import { Save, Loader2 } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <p className="text-ivory/40">Loading settings…</p>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setSocial = (k) => (e) => setS({ ...s, socials: { ...(s.socials || {}), [k]: e.target.value } });
  const setImage = (k, v) => setS({ ...s, images: { ...(s.images || {}), [k]: v } });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings" className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div><p className="text-gold/70 text-xs tracking-[0.25em] uppercase">Website Settings</p><h1 className="font-serif text-4xl text-ivory mt-1">Site Configuration</h1></div>
        <Btn onClick={save} disabled={saving} data-testid="save-settings">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saved ? "Saved!" : "Save Changes"}</Btn>
      </div>

      <div className="space-y-6">
        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-4">Brand & Contact</h3>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Brand Name"><Input value={s.brand_name || ""} onChange={set("brand_name")} /></Field>
            <Field label="Tagline"><Input value={s.tagline || ""} onChange={set("tagline")} /></Field>
            <Field label="Phone"><Input value={s.phone || ""} onChange={set("phone")} /></Field>
            <Field label="Email"><Input value={s.email || ""} onChange={set("email")} /></Field>
            <Field label="WhatsApp Number (digits only)"><Input value={s.whatsapp || ""} onChange={set("whatsapp")} /></Field>
            <Field label="Address"><Input value={s.address || ""} onChange={set("address")} /></Field>
          </div>
          <Field label="WhatsApp Pre-filled Message"><Textarea value={s.whatsapp_message || ""} onChange={set("whatsapp_message")} /></Field>
          <ImageUpload value={s.logo_url} onChange={(v) => setS({ ...s, logo_url: v })} label="Logo" />
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-4">Hero Section</h3>
          <Field label="Headline"><Input value={s.hero_headline || ""} onChange={set("hero_headline")} /></Field>
          <Field label="Sub-headline"><Textarea value={s.hero_sub || ""} onChange={set("hero_sub")} /></Field>
          <ImageUpload value={s.hero_image} onChange={(v) => setS({ ...s, hero_image: v })} label="Hero Background Image" />
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-4">Homepage Features</h3>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Flavour Collection Mode"><Select value={s.flavour_mode || "MENU"} onChange={set("flavour_mode")} options={[{ value: "MENU", label: "Menu Mode (Explore Our Menus)" }, { value: "GALLERY", label: "Gallery Mode (Explore Our World)" }]} /></Field>
            <Field label="Show 3D Experience"><Select value={s.show_3d ? "1" : "0"} onChange={(e) => setS({ ...s, show_3d: e.target.value === "1" })} options={[{ value: "1", label: "Enabled" }, { value: "0", label: "Hidden" }]} /></Field>
          </div>
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-4">Social Links</h3>
          <div className="grid sm:grid-cols-3 gap-x-4">
            <Field label="Instagram"><Input value={s.socials?.instagram || ""} onChange={setSocial("instagram")} /></Field>
            <Field label="Facebook"><Input value={s.socials?.facebook || ""} onChange={setSocial("facebook")} /></Field>
            <Field label="YouTube"><Input value={s.socials?.youtube || ""} onChange={setSocial("youtube")} /></Field>
          </div>
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-serif text-xl text-gold mb-1">Site Imagery</h3>
          <p className="text-ivory/40 text-sm mb-4">Replace any placeholder photo used across the website. Upload your own to swap them out.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
            {Object.keys(s.images || {}).map((k) => (
              <ImageUpload key={k} value={s.images[k]} onChange={(v) => setImage(k, v)} label={k.replace(/_/g, " ")} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
